/* INTU main product — gallery, purchase options (selling plans), sticky bar, jump nav.
   Add to cart itself is handled by the theme's <product-form> (assets/product.js).
   Several <intu-product> elements can share a page (main product + intu-product-offer); each one is
   self-contained: queries are scoped to the element, radios/forms use per-section ids. */
(() => {
  if (customElements.get('intu-product')) return;

  const formatMoney = (cents, format) => {
    cents = Math.round(Number(cents) || 0);
    const fmt = format || '${{amount}}';
    const withDelims = (n, precision, thousands, decimal) => {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      const fixed = (n / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(cents, 0);
        case 'amount_with_comma_separator': return withDelims(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(cents, 2, "'", '.');
        case 'amount_with_space_separator': return withDelims(cents, 2, ' ', ',');
        default: return withDelims(cents, 2);
      }
    });
  };

  class IntuProduct extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-imp-data]').textContent);
      } catch (e) {
        this.data = { variants: {}, planPercent: {} };
      }
      this.baseVariant = String(this.data.baseVariant || '');
      this.money = (c, cc) => formatMoney(c, cc ? this.data.moneyFormatCurrency : this.data.moneyFormat);

      this.initGallery();
      this.initOptions();
      this.initQty();
      this.initNotify();
      this.initSmooth();
      this.initStickyTop();
      this.initJump();
      this.initBar();
    }

    disconnectedCallback() {
      (this._cleanup || []).forEach((fn) => fn());
    }

    onWin(evt, fn, opts) {
      window.addEventListener(evt, fn, opts);
      (this._cleanup = this._cleanup || []).push(() => window.removeEventListener(evt, fn, opts));
    }

    /* ---------------- Gallery ---------------- */
    initGallery() {
      this.track = this.querySelector('[data-imp-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-imp-thumbs] [data-index]'));
      this.dots = Array.from(this.querySelectorAll('[data-imp-dots] [data-index]'));
      [...this.thumbs, ...this.dots].forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(Number(btn.dataset.index)));
      });
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
          this.setActive(i);
        });
      }, { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.current + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.current - 1); }
      });
      this.current = 0;
    }

    goTo(i) {
      if (!this.slides || !this.slides.length) return;
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
      this.setActive(i);
    }

    setActive(i) {
      if (i === this.current && this._activeSet) return;
      this._activeSet = true;
      this.current = i;
      const toggle = (el) => {
        const on = Number(el.dataset.index) === i;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-current', on ? 'true' : 'false');
      };
      this.thumbs.forEach(toggle);
      this.dots.forEach(toggle);
      const t = this.thumbs[i];
      const wrap = t && t.parentElement;
      if (wrap && wrap.scrollWidth > wrap.clientWidth) {
        wrap.scrollTo({ left: t.offsetLeft - wrap.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
      }
      this.slides.forEach((s, idx) => {
        if (idx !== i) s.querySelectorAll('video').forEach((v) => v.pause());
      });
    }

    goToMedia(mediaId) {
      if (!mediaId || !this.slides) return;
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx > -1) this.goTo(idx);
    }

    /* ---------------- Purchase options ---------------- */
    initOptions() {
      this.po = this.querySelector('[data-imp-po]');
      this.idInput = this.querySelector('[data-imp-id]');
      this.planInput = this.querySelector('[data-imp-plan]');
      this.submit = this.querySelector('[data-imp-submit]');
      this.form = this.submit ? this.submit.form : null;
      if (this.form) {
        /* Runs before the theme's product-form handler: keep only our id / selling_plan values
           (apps such as subscription widgets may inject their own inputs into product forms). */
        this.form.addEventListener('submit', () => this.syncForm(), true);
        /* Busy state while the add request runs; the theme's product-form only toggles a class. */
        this.form.addEventListener('submit', () => this.setBusy(true), true);
        const done = () => this.setBusy(false);
        document.addEventListener('cart:item-added', done);
        document.addEventListener('product:variant-error', done);
        (this._cleanup = this._cleanup || []).push(() => {
          document.removeEventListener('cart:item-added', done);
          document.removeEventListener('product:variant-error', done);
        });
      }
      this.qtyInput = this.querySelector('[data-imp-qty-input]');
      this.state = { variant: this.idInput ? this.idInput.value : this.baseVariant, plan: this.planInput && !this.planInput.disabled ? this.planInput.value : '', qty: 1 };

      if (this.po) {
        this.radios = Array.from(this.po.querySelectorAll('.imp-opt__input'));
        this.radios.forEach((r) => r.addEventListener('change', () => this.onOption(r)));
        this.po.querySelectorAll('[data-imp-variant]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.baseVariant = btn.dataset.impVariant;
            this.po.querySelectorAll('[data-imp-variant]').forEach((b) => {
              const on = b === btn;
              b.classList.toggle('is-active', on);
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            this.refreshCards();
            const r = this.selectedRadio();
            if (r) this.onOption(r, true); else this.apply(this.baseVariant, '', 0, true);
          });
        });
        this.po.querySelectorAll('[data-imp-freq]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const sub = this.radios.find((r) => r.dataset.kind === 'subscribe');
            if (!sub) return;
            sub.dataset.plan = btn.dataset.impFreq;
            this.po.querySelectorAll('[data-imp-freq]').forEach((b) => {
              const on = b === btn;
              b.classList.toggle('is-active', on);
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            const note = this.po.querySelector('[data-imp-freq-note]');
            if (note && this.po.dataset.freqNote) note.textContent = this.po.dataset.freqNote.replace('[plan]', btn.dataset.name || '');
            this.refreshCards();
            sub.checked = true;
            this.onOption(sub);
          });
        });
        const r = this.selectedRadio();
        if (r) this.onOption(r, false, true);
        this.buildBarSelect();
      }
      if (!this.po || !this.selectedRadio()) this.apply(this.state.variant, this.state.plan, 0, false, true);
    }

    /* Keep app-injected purchase widgets (e.g. Appstle subscriptions, which rewrite cart/add
       requests from their own selection) in step with our selected option. */
    syncForeign() {
      if (!this.form) return;
      const plan = this.state.plan || '';
      const radios = Array.from(this.form.querySelectorAll('input[type="radio"]')).filter((r) => !r.closest('[data-imp-po]'));
      const target = radios.find((r) => r.value === plan && /selling_plan/.test(r.name || ''));
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
        target.dispatchEvent(new Event('click', { bubbles: true }));
      }
      const widget = this.form.querySelector('[data-selected-selling-plan]');
      if (widget) widget.setAttribute('data-selected-selling-plan', plan);
    }

    syncForm() {
      if (!this.form) return;
      this.syncForeign();
      if (this.idInput) { this.idInput.value = this.state.variant; this.idInput.disabled = false; }
      if (this.planInput) {
        this.planInput.value = this.state.plan || '';
        this.planInput.disabled = !this.state.plan;
      }
      /* Multi-unit plans add the base variant × qty (see data-qty in intu-purchase-options.liquid). */
      if (this.qtyInput && this.qtyInput.type === 'hidden') {
        this.qtyInput.value = this.state.qty || 1;
        this.qtyInput.disabled = false;
      }
      this.pinAppstlePlan();
    }

    /* Appstle patches fetch / XHR and overwrites selling_plan on every /cart/add request with
       $('.appstle_sub_widget input[name=selling_plan]:checked').val() — a document-wide lookup that
       ignores which form was submitted (and is empty for plans its widget has not "checked").
       The theme's product-form fetches synchronously inside the submit event, so for the duration of
       this submit we put a checked radio carrying OUR plan first in the document, then remove it. */
    pinAppstlePlan() {
      if (!document.querySelector('.appstle_sub_widget') && !window.RSConfig) return;
      const pin = document.createElement('div');
      pin.className = 'appstle_sub_widget';
      pin.hidden = true;
      pin.setAttribute('data-imp-plan-pin', '');
      const r = document.createElement('input');
      r.type = 'radio';
      r.name = 'selling_plan';
      r.value = this.state.plan || '';
      r.checked = true;
      r.tabIndex = -1;
      pin.appendChild(r);
      document.querySelectorAll('[data-imp-plan-pin]').forEach((old) => old.remove());
      document.body.insertBefore(pin, document.body.firstChild);
      /* Appstle reads the selection asynchronously, so keep the pin until the add has finished. */
      const drop = () => { pin.remove(); document.removeEventListener('cart:updated', drop); };
      document.addEventListener('cart:updated', drop);
      setTimeout(drop, 5000);
    }

    selectedRadio() {
      return this.radios ? this.radios.find((r) => r.checked) : null;
    }

    /* Sticky bar: a native select mirroring the purchase-option cards, so the plan can be chosen
       from the bar without scrolling back up. Changing it drives the same code path as the cards. */
    buildBarSelect() {
      const sel = this.querySelector('[data-imp-bar-select]');
      if (!sel || !this.radios || this.radios.length < 2) return;
      sel.innerHTML = '';
      this.radios.forEach((r, i) => {
        const o = document.createElement('option');
        o.value = String(i);
        sel.appendChild(o);
      });
      sel.addEventListener('change', () => {
        const r = this.radios[Number(sel.value)];
        if (!r || r.disabled) return;
        r.checked = true;
        this.onOption(r);
      });
      sel.hidden = false;
      this.syncBarSelect();
    }

    syncBarSelect() {
      const sel = this.querySelector('[data-imp-bar-select]');
      if (!sel || sel.hidden || !this.radios) return;
      this.radios.forEach((r, i) => {
        const o = sel.options[i];
        if (!o) return;
        const card = r.closest('.imp-opt');
        const name = card.querySelector('[data-imp-opt-name]');
        const price = card.querySelector('[data-imp-opt-price]');
        o.textContent = (name ? name.textContent.trim() : r.value) + (price ? ' · ' + price.textContent.trim() : '');
        o.disabled = r.disabled;
      });
      const i = this.radios.findIndex((r) => r.checked);
      if (i >= 0 && sel.value !== String(i)) sel.value = String(i);
    }

    /* ---------------- Waitlist → Klaviyo ---------------- */
    /* Forms with data-imp-klaviyo-list post to Klaviyo's client subscriptions API; the public key comes
       from the form (theme setting) or from the Klaviyo onsite embed already on the page. */
    initNotify() {
      this.querySelectorAll('form[data-imp-klaviyo-list]').forEach((form) => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (!form.reportValidity()) return;
          const input = form.querySelector('input[type="email"]');
          const email = input ? input.value.trim() : '';
          const ok = form.querySelector('[data-imp-notify-ok]');
          const err = form.querySelector('[data-imp-notify-err]');
          const btn = form.querySelector('button[type="submit"]');
          const key = form.dataset.impKlaviyoKey || this.klaviyoKey();
          if (!email || !key) { if (err) err.hidden = false; return; }
          if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
          try {
            const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    custom_source: form.dataset.impKlaviyoSource || 'Website waitlist',
                    profile: { data: { type: 'profile', attributes: { email } } },
                  },
                  relationships: { list: { data: { type: 'list', id: form.dataset.impKlaviyoList } } },
                },
              }),
            });
            if (!res.ok) throw new Error('Klaviyo ' + res.status);
            const row = form.querySelector('.imp-notify__row');
            const label = form.querySelector('.imp-notify__label');
            if (row) row.hidden = true;
            if (label) label.hidden = true;
            if (err) err.hidden = true;
            if (ok) ok.hidden = false;
          } catch (x) {
            if (err) err.hidden = false;
            if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
          }
        });
      });
    }

    klaviyoKey() {
      const src = Array.from(document.scripts).map((s) => s.src || '').find((u) => /klaviyo\.js\?company_id=/.test(u));
      const m = src && src.match(/company_id=([A-Za-z0-9]+)/);
      return m ? m[1] : '';
    }

    setBusy(on) {
      clearTimeout(this._busyTimer);
      [this.submit, this.querySelector('[data-imp-bar-btn]')].forEach((b) => {
        if (!b) return;
        b.setAttribute('aria-busy', on ? 'true' : 'false');
        if (on) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
      });
      /* Network failure fires no event: never leave the button stuck. */
      if (on) this._busyTimer = setTimeout(() => this.setBusy(false), 8000);
    }

    /* qty > 1 = a multi-unit option on a single-variant product; prices describe the whole pack.
       pct > 0 = a quantity tier without a plan (its discount exists as an automatic discount in Shopify). */
    optionPrices(variantId, planId, qty, pct) {
      const v = this.data.variants[variantId];
      if (!v) return null;
      const q = Math.max(1, Number(qty) || 1);
      const off = Number(pct) || 0;
      if (planId) {
        const a = v.plans[planId];
        if (!a) return { price: v.price * q, compare: 0, available: false };
        let compare = a[1];
        if (this.po && this.po.dataset.compare === 'rrp' && v.compare > compare) compare = v.compare;
        return { price: a[0] * q, compare: compare * q, available: v.available };
      }
      if (off > 0) return { price: Math.round((v.price * q * (100 - off)) / 100), compare: v.price * q, available: v.available };
      return { price: v.price * q, compare: v.compare * q, available: v.available };
    }

    refreshCards() {
      if (!this.radios) return;
      const cc = this.po.dataset.currency === 'true';
      this.radios.forEach((r) => {
        const vid = r.dataset.variant || this.baseVariant;
        const p = this.optionPrices(vid, r.dataset.plan, r.dataset.qty, r.dataset.pct);
        if (!p) return;
        const card = r.closest('.imp-opt');
        const kind = r.dataset.kind;
        const priceEl = card.querySelector('[data-imp-opt-price]');
        const cmpEl = card.querySelector('[data-imp-opt-compare]');
        const serveEl = card.querySelector('[data-imp-opt-serve]');
        const saveEl = card.querySelector('[data-imp-opt-save]');
        if (priceEl) priceEl.textContent = this.money(p.price, cc);
        if (cmpEl) {
          const show = p.compare > p.price && !(kind === 'onetime' && this.po.dataset.onetimeCompare !== 'true');
          cmpEl.hidden = !show;
          cmpEl.textContent = show ? this.money(p.compare, cc) : '';
        }
        const serves = Number(r.dataset.serves) || 0;
        if (serveEl && serves > 0 && this.po.dataset.serve) {
          serveEl.textContent = this.po.dataset.serve.replace('[price]', this.money(Math.round(p.price / serves)));
          serveEl.hidden = false;
        }
        const pct = p.compare > p.price ? Math.round(((p.compare - p.price) * 100) / p.compare) : 0;
        const savingsFmt = r.dataset.savings || this.po.dataset.savings;
        if (saveEl && (kind === 'plan' || kind === 'subscribe' || kind === 'qty') && savingsFmt) {
          const units = Number(r.dataset.units) || 0;
          saveEl.hidden = !(p.compare > p.price);
          saveEl.textContent = savingsFmt
            .replace('[amount]', this.money(p.compare - p.price))
            .replace('[percent]', pct)
            .replace('[per_unit]', this.money(units > 0 ? Math.round(p.price / units) : p.price));
        }
        if (kind === 'subscribe' && this.po.dataset.subscribe) {
          const planPct = this.data.planPercent[r.dataset.plan];
          const name = card.querySelector('[data-imp-opt-name]');
          if (name) name.textContent = this.po.dataset.subscribe.replace('[percent]', planPct != null ? planPct : pct);
        }
        r.disabled = !p.available;
        card.classList.toggle('is-disabled', !p.available);
      });
    }

    onOption(radio, fromVariant, initial) {
      this.radios.forEach((r) => r.closest('.imp-opt').classList.toggle('is-selected', r === radio));
      const variant = radio.dataset.variant || this.baseVariant;
      const plan = radio.dataset.plan || '';
      this.apply(variant, plan, Number(radio.dataset.serves) || 0, fromVariant, initial, Number(radio.dataset.qty) || 1, Number(radio.dataset.pct) || 0);
    }

    apply(variantId, planId, serves, fromVariant, initial, qty, pct) {
      variantId = String(variantId);
      qty = Math.max(1, Number(qty) || 1);
      pct = Number(pct) || 0;
      const prevVariant = this.state.variant;
      this.state = { variant: variantId, plan: planId, serves, qty, pct };
      if (this.idInput) this.idInput.value = variantId;
      if (this.planInput) {
        this.planInput.value = planId || '';
        this.planInput.disabled = !planId;
      }
      if (this.qtyInput && this.qtyInput.type === 'hidden') this.qtyInput.value = qty;
      this.syncForeign();
      const opt = this.optionPrices(variantId, planId, qty, pct);
      const v = this.data.variants[variantId];
      if (!opt || !v) return;

      /* price blocks */
      this.querySelectorAll('[data-imp-price]').forEach((el) => {
        const cc = el.dataset.currency === 'true';
        const p = el.dataset.source === 'option' ? opt : { price: v.price, compare: v.compare };
        const now = el.querySelector('[data-imp-price-now]');
        const was = el.querySelector('[data-imp-price-was]');
        const suf = el.querySelector('[data-imp-price-suffix]');
        const badge = el.querySelector('[data-imp-price-badge]');
        if (now) now.textContent = this.money(p.price, cc);
        if (was) {
          was.hidden = !(p.compare > p.price);
          was.textContent = p.compare > p.price ? this.money(p.compare, cc) : '';
        }
        const s = serves || Number(el.dataset.serves) || 0;
        if (suf && s > 0 && el.dataset.suffix) {
          suf.textContent = el.dataset.suffix.replace('[price]', this.money(Math.round(p.price / s)));
          suf.hidden = false;
        }
        if (badge && el.dataset.badge) {
          const pct = p.compare > p.price ? Math.round(((p.compare - p.price) * 100) / p.compare) : 0;
          badge.hidden = pct <= 0;
          badge.textContent = el.dataset.badge.replace('[percent]', pct);
        }
      });

      /* button */
      const available = opt.available !== false && v.available;
      if (this.submit) {
        const txt = this.submit.querySelector('[data-imp-btn-text]');
        const sep = this.submit.dataset.sep || '';
        if (txt) {
          txt.textContent = available
            ? this.submit.dataset.label + (this.submit.dataset.showPrice === 'true' ? sep + this.money(opt.price) : '')
            : this.submit.dataset.soldout;
        }
        this.submit.disabled = !available;
      }
      const barBtn = this.querySelector('[data-imp-bar-btn]');
      if (barBtn) barBtn.disabled = !available;
      const barPrice = this.querySelector('[data-imp-bar-price]');
      if (barPrice) barPrice.textContent = this.money(opt.price);
      /* Sticky bar: say which option it adds ("3 tubs · 84 day supply · subscription"). */
      const barPlan = this.querySelector('[data-imp-bar-plan]');
      if (barPlan) {
        const sel = this.selectedRadio();
        const nameEl = sel ? sel.closest('.imp-opt').querySelector('[data-imp-opt-name]') : null;
        const name = nameEl ? nameEl.textContent.trim() : '';
        barPlan.textContent = name ? name + (planId ? ' · subscription' : ' · one-time') : '';
      }
      this.syncBarSelect();

      /* installments */
      this.querySelectorAll('[data-imp-inst]').forEach((el) => {
        const t = el.querySelector('[data-imp-inst-text]');
        const n = Number(el.dataset.count) || 4;
        if (t) t.textContent = el.dataset.format.replace('[amount]', this.money(Math.round(opt.price / n))).replace('[count]', n);
      });

      if (initial) return;
      if (variantId !== prevVariant && v.media) this.goToMedia(v.media);
      /* data-history="false": secondary widgets (e.g. the offer section) leave the URL alone */
      if (this.dataset.history !== 'false' && (!window.Shopify || !window.Shopify.designMode)) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          if (planId) url.searchParams.set('selling_plan', planId); else url.searchParams.delete('selling_plan');
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* noop */ }
      }
    }

    /* ---------------- Quantity ---------------- */
    initQty() {
      const q = this.querySelector('[data-imp-qty]');
      if (!q) return;
      const input = q.querySelector('input');
      q.querySelectorAll('[data-step]').forEach((b) => {
        b.addEventListener('click', () => {
          input.value = Math.max(1, (Number(input.value) || 1) + Number(b.dataset.step));
        });
      });
    }

    /* ---------------- Anchors ---------------- */
    initSmooth() {
      this.querySelectorAll('a[data-imp-smooth]').forEach((a) => {
        a.addEventListener('click', (e) => {
          const href = a.getAttribute('href') || '';
          if (!href.startsWith('#') || href.length < 2) return;
          let target = null;
          try { target = document.querySelector(href); } catch (err) { target = null; }
          if (!target) return;
          e.preventDefault();
          const offset = this.headerOffset() + (this.jump ? this.jump.offsetHeight : 0) + 12;
          const y = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: y, behavior: 'smooth' });
          const input = target.querySelector && target.querySelector('input[type=email]');
          if (input) setTimeout(() => input.focus({ preventScroll: true }), 600);
        });
      });
    }

    headerOffset() {
      const header = document.querySelector('.intu-header-section') || document.querySelector('[id*="__header"]');
      if (!header) return 0;
      const pos = getComputedStyle(header).position;
      if (pos !== 'sticky' && pos !== 'fixed') return 0;
      const r = header.getBoundingClientRect();
      return Math.max(0, r.bottom > 0 ? r.bottom : 0);
    }

    initStickyTop() {
      const set = () => this.style.setProperty('--imp-sticky-top', this.headerOffset() + 'px');
      set();
      this.onWin('resize', set, { passive: true });
      this.onWin('scroll', set, { passive: true });
    }

    /* ---------------- Jump nav ---------------- */
    initJump() {
      this.jump = this.querySelector('[data-imp-jump]');
      if (!this.jump) return;
      const holder = this.querySelector('[data-imp-jump-holder]');
      const pills = Array.from(this.jump.querySelectorAll('a[href^="#"]'));
      if (this.jump.classList.contains('imp-jump--sticky')) {
        const onScroll = () => {
          const top = this.headerOffset();
          const rect = holder.getBoundingClientRect();
          const fixed = rect.top <= top;
          if (fixed !== this.jump.classList.contains('is-fixed')) {
            holder.style.height = fixed ? this.jump.offsetHeight + 'px' : '';
            this.jump.classList.toggle('is-fixed', fixed);
          }
        };
        onScroll();
        this.onWin('scroll', onScroll, { passive: true });
        this.onWin('resize', onScroll, { passive: true });
      }
      const targets = pills.map((p) => {
        try { return document.querySelector(p.getAttribute('href')); } catch (e) { return null; }
      });
      if (!('IntersectionObserver' in window) || !targets.some(Boolean)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const i = targets.indexOf(en.target);
          pills.forEach((p, idx) => p.classList.toggle('is-active', idx === i));
        });
      }, { rootMargin: '-40% 0px -55% 0px' });
      targets.forEach((t) => t && io.observe(t));
      (this._cleanup = this._cleanup || []).push(() => io.disconnect());
    }

    /* ---------------- Sticky bar ---------------- */
    initBar() {
      this.bar = this.querySelector('[data-imp-bar]');
      if (!this.bar) return;
      const anchor = this.submit || this.querySelector('.imp-notify') || null;
      if (!anchor || !('IntersectionObserver' in window)) return;
      let inView = true;
      const update = () => {
        const show = !inView && window.scrollY > 300;
        this.bar.classList.toggle('is-visible', show);
        this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.bar.querySelectorAll('button, a, select').forEach((el) => el.setAttribute('tabindex', show ? '0' : '-1'));
      };
      const io = new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
        update();
      });
      io.observe(anchor);
      this.onWin('scroll', update, { passive: true });
      (this._cleanup = this._cleanup || []).push(() => io.disconnect());
    }
  }

  customElements.define('intu-product', IntuProduct);
})();
